<script setup lang="ts">
/**
 * Module: web/src/views/QuickShellView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-07-08
 *
 * Dedicated limited shell for PIN sessions (doc 04 sect 6.5 step 6):
 * a VISIBLY LIMITED surface -- banner, NO nav, NO account menu, logout only.
 * Shows ONLY the placeholders for the caps inside the PIN scope
 * (data_entry -> P4 MO entry; calendar -> v2). A PIN session never sees any
 * role Workspace (sect 9.5) and the guard confines it here.
 */
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import AppIcon from '@/components/AppIcon.vue';

const auth = useAuthStore();
const router = useRouter();

const hasDataEntry = computed(() => auth.pinCaps.includes('data_entry'));
const hasCalendar = computed(() => auth.pinCaps.includes('calendar'));

async function signOut() {
  await auth.logout();
  void router.push('/pin');
}
</script>

<template>
  <div class="min-h-screen flex flex-col">
    <!-- Distinct limited-session banner -->
    <div class="bg-accent-warm/15 border-b border-accent-warm/40 px-4 py-2.5 flex items-center gap-2.5">
      <AppIcon name="alert" class="w-5 h-5 text-accent-warm flex-shrink-0" />
      <span class="text-sm font-semibold text-accent-warm">Quick access — limited</span>
    </div>

    <!-- Minimal top bar: who + logout. No nav, no account menu. -->
    <header class="flex items-center justify-between gap-3 px-4 py-3 bg-card border-b border-border-c">
      <div class="flex items-center gap-2.5 min-w-0">
        <AppIcon name="user" class="w-5 h-5 text-fg-muted flex-shrink-0" />
        <span class="text-sm text-fg truncate">{{ auth.person?.name ?? 'Quick access' }}</span>
      </div>
      <button class="btn-secondary !h-10 !px-3 text-sm" @click="signOut">
        <AppIcon name="logout" class="w-4 h-4" /> Log out
      </button>
    </header>

    <main class="flex-1 px-4 py-6 max-w-lg w-full mx-auto space-y-4">
      <p class="text-sm text-fg-muted">
        This quick-access session covers only the surfaces below. For anything else,
        sign in with your full account.
      </p>

      <div v-if="hasDataEntry" class="bg-card border border-border-c rounded-lg p-5 shadow-card">
        <div class="flex items-center gap-2.5 mb-1.5">
          <AppIcon name="plus" class="w-5 h-5 text-accent" />
          <h2 class="font-semibold text-fg">MO Entry</h2>
        </div>
        <p class="text-sm text-fg-muted">Arrives in P4 — quick data entry will live here.</p>
      </div>

      <div v-if="hasCalendar" class="bg-card border border-border-c rounded-lg p-5 shadow-card">
        <div class="flex items-center gap-2.5 mb-1.5">
          <AppIcon name="list" class="w-5 h-5 text-accent" />
          <h2 class="font-semibold text-fg">Calendar</h2>
        </div>
        <p class="text-sm text-fg-muted">Arrives in v2 — the calendar subsystem is deferred.</p>
      </div>

      <div v-if="!hasDataEntry && !hasCalendar" class="alert-info">
        This PIN has no active surfaces yet. Ask a manager to check its scope.
      </div>
    </main>
  </div>
</template>
